import { useRef, useSyncExternalStore } from "react";
import { type GridRowId } from "@mui/x-data-grid-pro";

// チェック状態を React state でなく外部ストアに置く。
// 5万行規模ではチェック1回で columns 再生成→全セル再描画になると操作が重くなるため、
// セル側が自列の Set だけを購読して、変更のあった列のセルのみ再描画させる

export interface CheckedIdsStore {
  subscribe: (listener: () => void) => () => void;
  /** 変更のたびに増える版数（画面全体の集計表示の再計算用） */
  getVersion: () => number;
  getAll: () => Record<string, Set<GridRowId>>;
  getField: (field: string) => Set<GridRowId>;
  /** 指定列のうち、その行 ID がチェック済みの列だけを返す（全部事項の現状構成の取り出し用） */
  getRowFields: (fields: readonly string[], id: GridRowId) => readonly string[];
  toggle: (field: string, id: GridRowId) => void;
  setField: (field: string, ids: Set<GridRowId>) => void;
  /**
   * 1行〜複数行分の複数列のチェックを一度に置き換える
   * （全部事項を候補の構成にそろえる用、列ヘッダーのダブルクリック一括切り替え用）。
   * 列ごと・行ごとに個別に呼ぶと途中の不正な組み合わせが購読側に見えたり通知が行数分に増えたりするため、1回の通知にまとめる
   */
  setRowFieldsBulk: (
    fields: readonly string[],
    entries: ReadonlyArray<{ id: GridRowId; checkedFields: readonly string[] }>
  ) => void;
  /** 指定列から shouldRemove が true を返す ID を取り除き、取り除いた件数を返す（変更があった場合のみ通知） */
  removeWhere: (
    field: string,
    shouldRemove: (id: GridRowId) => boolean
  ) => number;
}

const EMPTY_SET = new Set<GridRowId>();

const createCheckedIdsStore = (): CheckedIdsStore => {
  let state: Record<string, Set<GridRowId>> = {};
  let version = 0;
  const listeners = new Set<() => void>();
  const emit = (): void => {
    version += 1;
    listeners.forEach((listener) => {
      listener();
    });
  };
  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getVersion: () => version,
    getAll: () => state,
    getField: (field) => state[field] ?? EMPTY_SET,
    getRowFields: (fields, id) =>
      fields.filter((field) => (state[field] ?? EMPTY_SET).has(id)),
    toggle: (field, id) => {
      const ids = new Set(state[field]);
      if (ids.has(id)) {
        ids.delete(id);
      } else {
        ids.add(id);
      }
      state = { ...state, [field]: ids };
      emit();
    },
    setField: (field, ids) => {
      state = { ...state, [field]: ids };
      emit();
    },
    setRowFieldsBulk: (fields, entries) => {
      const next = { ...state };
      for (const field of fields) {
        next[field] = new Set(state[field]);
      }
      for (const { id, checkedFields } of entries) {
        for (const field of fields) {
          if (checkedFields.includes(field)) {
            next[field].add(id);
          } else {
            next[field].delete(id);
          }
        }
      }
      state = next;
      emit();
    },
    removeWhere: (field, shouldRemove) => {
      const ids = state[field] ?? EMPTY_SET;
      const kept = [...ids].filter((id) => !shouldRemove(id));
      const removed = ids.size - kept.length;
      if (removed > 0) {
        state = { ...state, [field]: new Set(kept) };
        emit();
      }
      return removed;
    },
  };
};

/** 画面マウント中は同一インスタンスを保つチェック状態ストア */
export const useCheckedIdsStore = (): CheckedIdsStore => {
  const storeRef = useRef<CheckedIdsStore>();
  storeRef.current ??= createCheckedIdsStore();
  return storeRef.current;
};

/** 指定列のチェック済み ID 集合を購読する（他列の変更では再描画されない） */
export const useCheckedField = (
  store: CheckedIdsStore,
  field: string
): Set<GridRowId> =>
  useSyncExternalStore(store.subscribe, () => store.getField(field));

/** チェック状態の版数を購読する（件数集計など画面全体の再計算用） */
export const useCheckedVersion = (store: CheckedIdsStore): number =>
  useSyncExternalStore(store.subscribe, store.getVersion);
